import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ShieldCheck, Plus, Pencil, Trash2, AlertTriangle, CalendarX2, FileWarning, Gauge } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['SETA', 'QCTO', 'ISO', 'POPIA', 'OHS', 'Policy', 'SOP', 'Accreditation', 'Other'];
const STATUS_META = {
  compliant: { label: 'Compliant', cls: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  in_progress: { label: 'In Progress', cls: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400' },
  non_compliant: { label: 'Non-Compliant', cls: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400' },
  closed: { label: 'Closed', cls: 'bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400' },
};

const EMPTY = { requirement: '', category: 'SETA', status: 'in_progress', evidence: '', due_date: '', review_date: '', expiry_date: '', finding: '', finding_status: 'none', corrective_action: '', capa_due_date: '', capa_status: 'none', notes: '' };

const StatCard = ({ icon: Icon, label, value, tone, testid }) => (
  <Card className="!p-4" data-testid={testid}>
    <CardContent className="p-0 flex items-center gap-3">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${tone}`}><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-2xl font-bold font-mono leading-tight">{value ?? '—'}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
      </div>
    </CardContent>
  </Card>
);

export default function CompliancePage() {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  const [catFilter, setCatFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(async () => {
    try {
      const [i, d] = await Promise.all([
        axios.get(`${API}/compliance/items`),
        axios.get(`${API}/compliance/dashboard`),
      ]);
      setItems(i.data);
      setDash(d.data);
    } catch (e) {
      toast.error('Failed to load compliance data');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setShowForm(true); };
  const openEdit = (item) => {
    setEditing(item);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(item).filter(([k]) => k in EMPTY).map(([k, v]) => [k, v ?? ''])),
      finding_status: item.finding_status || 'none', capa_status: item.capa_status || 'none' });
    setShowForm(true);
  };

  const save = async () => {
    if (!form.requirement.trim()) return toast.error('Requirement is required');
    const payload = { ...form,
      finding_status: form.finding_status === 'none' ? null : form.finding_status,
      capa_status: form.capa_status === 'none' ? null : form.capa_status,
      due_date: form.due_date || null, review_date: form.review_date || null,
      expiry_date: form.expiry_date || null, capa_due_date: form.capa_due_date || null };
    try {
      if (editing) await axios.put(`${API}/compliance/items/${editing.id}`, payload);
      else await axios.post(`${API}/compliance/items`, payload);
      toast.success(editing ? 'Compliance item updated' : 'Compliance item created');
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.requirement}"?`)) return;
    try {
      await axios.delete(`${API}/compliance/items/${item.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const filtered = items.filter(i =>
    (catFilter === 'all' || i.category === catFilter) &&
    (statusFilter === 'all' || i.status === statusFilter));

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="compliance-title">
            <ShieldCheck className="h-7 w-7 text-primary" /> Compliance
          </h1>
          <p className="text-muted-foreground mt-1">SETA · QCTO · ISO · POPIA · OHS · Policies · Accreditation</p>
        </div>
        <Button className="rounded-xl gap-2" onClick={openCreate} data-testid="new-compliance-btn">
          <Plus className="h-4 w-4" /> New Item
        </Button>
      </div>

      {/* Dashboard */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard icon={Gauge} label="Compliance Score" value={dash?.score != null ? `${dash.score}%` : 'No data'}
          tone="bg-blue-100 dark:bg-blue-500/10 text-blue-600" testid="comp-score" />
        <StatCard icon={FileWarning} label="Open Findings" value={dash?.open_findings}
          tone="bg-amber-100 dark:bg-amber-500/10 text-amber-600" testid="comp-findings" />
        <StatCard icon={CalendarX2} label="Overdue Items" value={dash?.overdue}
          tone="bg-rose-100 dark:bg-rose-500/10 text-rose-600" testid="comp-overdue" />
        <StatCard icon={AlertTriangle} label="Expiring (30d)" value={dash?.expiring_30d}
          tone="bg-orange-100 dark:bg-orange-500/10 text-orange-600" testid="comp-expiring" />
        <StatCard icon={ShieldCheck} label="Overdue CAPAs" value={dash?.overdue_capas}
          tone="bg-violet-100 dark:bg-violet-500/10 text-violet-600" testid="comp-capas" />
      </div>

      {/* Filters */}
      <div className="flex gap-3 flex-wrap">
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-44" data-testid="comp-cat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="comp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card className="!p-0">
        <CardContent className="p-0 divide-y">
          {filtered.length ? filtered.map(item => {
            const expiringSoon = item.expiry_date && item.expiry_date >= today &&
              item.expiry_date <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
            const overdue = item.due_date && item.due_date < today && !['compliant', 'closed'].includes(item.status);
            return (
              <div key={item.id} className="flex items-center gap-3 p-4 hover:bg-muted/40" data-testid={`comp-row-${item.id}`}>
                <Badge variant="outline" className="shrink-0">{item.category}</Badge>
                <div className="min-w-0 flex-1">
                  <p className="font-medium truncate">{item.requirement}</p>
                  <p className="text-xs text-muted-foreground truncate">
                    {item.owner_name ? `${item.owner_name} · ` : ''}
                    {item.due_date ? `Due ${item.due_date}` : 'No due date'}
                    {item.expiry_date ? ` · Expires ${item.expiry_date}` : ''}
                  </p>
                </div>
                {overdue && <Badge className="bg-rose-100 dark:bg-rose-500/10 text-rose-600 shrink-0" variant="secondary">Overdue</Badge>}
                {expiringSoon && <Badge className="bg-orange-100 dark:bg-orange-500/10 text-orange-600 shrink-0" variant="secondary">Expiring</Badge>}
                {item.finding_status === 'open' && <Badge className="bg-amber-100 dark:bg-amber-500/10 text-amber-700 shrink-0" variant="secondary">Finding</Badge>}
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${STATUS_META[item.status]?.cls}`}>
                  {STATUS_META[item.status]?.label || item.status}
                </span>
                <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => openEdit(item)} data-testid={`comp-edit-${item.id}`}>
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-500 shrink-0" onClick={() => remove(item)} data-testid={`comp-delete-${item.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            );
          }) : (
            <div className="p-10 text-center text-muted-foreground" data-testid="compliance-empty">
              <ShieldCheck className="h-10 w-10 mx-auto mb-2 opacity-40" />
              No compliance items yet — add SETA, QCTO, ISO, POPIA or OHS requirements to start tracking.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Form dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="compliance-form-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Compliance Item</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>Requirement *</Label>
              <Input value={form.requirement} onChange={e => setForm({ ...form, requirement: e.target.value })} data-testid="comp-requirement-input" />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="comp-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="comp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} data-testid="comp-due-input" />
            </div>
            <div>
              <Label>Review Date</Label>
              <Input type="date" value={form.review_date} onChange={e => setForm({ ...form, review_date: e.target.value })} />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm({ ...form, expiry_date: e.target.value })} data-testid="comp-expiry-input" />
            </div>
            <div className="sm:col-span-2">
              <Label>Evidence (link or description)</Label>
              <Input value={form.evidence} onChange={e => setForm({ ...form, evidence: e.target.value })} />
            </div>
            <div className="sm:col-span-2 border-t pt-3">
              <Label>Finding</Label>
              <Textarea value={form.finding} onChange={e => setForm({ ...form, finding: e.target.value })} placeholder="Audit finding (if any)" />
            </div>
            <div>
              <Label>Finding Status</Label>
              <Select value={form.finding_status} onValueChange={v => setForm({ ...form, finding_status: v })}>
                <SelectTrigger data-testid="comp-finding-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No finding</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CAPA Status</Label>
              <Select value={form.capa_status} onValueChange={v => setForm({ ...form, capa_status: v })}>
                <SelectTrigger data-testid="comp-capa-status"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No CAPA</SelectItem>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="done">Done</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Corrective Action (CAPA)</Label>
              <Textarea value={form.corrective_action} onChange={e => setForm({ ...form, corrective_action: e.target.value })} />
            </div>
            <div>
              <Label>CAPA Due Date</Label>
              <Input type="date" value={form.capa_due_date} onChange={e => setForm({ ...form, capa_due_date: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button onClick={save} data-testid="comp-save-btn">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
